import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import config from "./config";
import OTPInput from "./components/OTPInput";
import './styles/Login.css';

function Login({ setUser }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check for access restriction error from URL params (Google OAuth)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const message = urlParams.get('message');
    
    if (error === 'access_restricted' && message) {
      setErrorMessage(decodeURIComponent(message));
      setShowError(true);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Add class to body for auth page scrolling
    document.body.classList.add('auth-page-active');
    
    return () => {
      document.body.classList.remove('auth-page-active');
    };
  }, []);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email) {
      setErrorMessage("Please enter your email address");
      setShowError(true);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${config.apiUrl}/api/auth/send-login-otp`, {
        email
      });
      setOtpSent(true);
      setErrorMessage("");
      setShowError(false);
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Failed to send OTP";
      setErrorMessage(errorMsg);
      setShowError(true);
      // If access is restricted, don't proceed to OTP screen
      if (err.response?.status === 403) {
        setOtpSent(false);
      }
    }
    setLoading(false);
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp) {
      setErrorMessage("Please enter the OTP");
      setShowError(true);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${config.apiUrl}/api/auth/verify-login-otp`, {
        email,
        otp
      });
      
      // Fetch privacy settings for the user
      try {
        const privacyRes = await axios.get(`${config.apiUrl}/api/auth/privacy-settings/${res.data.user.id}`);
        const userWithPrivacy = {
          ...res.data.user,
          privacySettings: privacyRes.data.privacySettings
        };
        setUser(userWithPrivacy);
        localStorage.setItem("chatapp_user", JSON.stringify(userWithPrivacy));
      } catch (privacyErr) {
        console.error('Failed to load privacy settings:', privacyErr);
        // Continue with user data without privacy settings
        setUser(res.data.user);
        localStorage.setItem("chatapp_user", JSON.stringify(res.data.user));
      }
      
      navigate('/');
    } catch (err) {
      setErrorMessage(err.response?.data?.error || "Invalid OTP");
      setShowError(true);
    }
    setLoading(false);
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      await axios.post(`${config.apiUrl}/api/auth/send-login-otp`, {
        email
      });
      setErrorMessage("OTP resent successfully");
      setShowError(true);
    } catch (err) {
      setErrorMessage(err.response?.data?.error || "Failed to resend OTP");
      setShowError(true);
    }
    setLoading(false);
  };

  const handleCloseError = () => setShowError(false);

  const handleQuickLogin = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setErrorMessage("Please enter your email address");
      setShowError(true);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${config.apiUrl}/api/auth/quick-login`, {
        email
      });
      
      // Fetch privacy settings for the user
      try {
        const privacyRes = await axios.get(`${config.apiUrl}/api/auth/privacy-settings/${res.data.user.id}`);
        const userWithPrivacy = {
          ...res.data.user,
          privacySettings: privacyRes.data.privacySettings
        };
        setUser(userWithPrivacy);
        localStorage.setItem("chatapp_user", JSON.stringify(userWithPrivacy));
      } catch (privacyErr) {
        console.error('Failed to load privacy settings:', privacyErr);
        // Continue with user data without privacy settings
        setUser(res.data.user);
        localStorage.setItem("chatapp_user", JSON.stringify(res.data.user));
      }
      
      navigate('/');
    } catch (err) {
      setErrorMessage(err.response?.data?.error || "Quick login failed");
      setShowError(true);
    }
    setLoading(false);
  };

  return (
    <div className="auth-page">
      <div className="auth-card-grid">
        <div className="auth-card-info">
          <div className="auth-logo-chip">UcmaApp</div>
          <h1>Welcome back to UcmaApp</h1>
          <p>
            Jump right into your conversations, manage groups, and pick up calls across every device.
          </p>
          {/* <ul className="auth-benefits">
            <li>Lightning-fast OTP login flow</li>
            <li>Seamless syncing between web and mobile</li>
            <li>Granular privacy controls for every chat</li>
          </ul> */}
        </div>

        <div className="auth-container auth-container--panel">
          <h2>Login</h2>

          {config.atozasSso?.enabled && (
            <>
              <button
                type="button"
                className="atozas-btn"
                onClick={() => {
                  window.location.href = `${config.apiUrl}/auth/atozas`;
                }}
              >
                Continue with ATOZAS Login
              </button>
              <div className="auth-divider">
                <span>OR</span>
              </div>
            </>
          )}

          {!otpSent ? (
            <form onSubmit={handleSendOTP}>
              <input
                name="email"
                type="email"
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
              <button type="submit" disabled={loading}>
                  {loading ? "Sending..." : "Send OTP Login"}
              </button>

              <div className="auth-divider">
                <span>OR</span>
              </div>

              <button 
                type="button" 
                className="google-btn"
                onClick={() => {
                  window.location.href = `${config.apiUrl}/api/auth/google`;
                }}
              >
                <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google Login
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP}>
              <div className="auth-helper-text">
                OTP sent to {email}
              </div>
              <OTPInput value={otp} onChange={setOtp} length={4} />
              <button type="submit" disabled={loading}>
                {loading ? "Verifying..." : "Verify OTP"}
              </button>
              <button 
                type="button" 
                onClick={handleResendOTP}
                disabled={loading}
                className="auth-ghost-btn"
              >
                Resend OTP
              </button>

              <div className="auth-divider">
                <span>OR</span>
              </div>

              <button 
                type="button" 
                className="google-btn"
                onClick={() => {
                  window.location.href = `${config.apiUrl}/api/auth/google`;
                }}
              >
                <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google Login
              </button>
            </form>
          )}

          <div className="auth-switch">
            <span>Don't have an account?</span>
            <Link to="/register">Register</Link>
          </div>
          
          <p className="auth-legal-text">
            By signing in, you agree to our{" "}
            <Link to="/terms">Terms of Service</Link> and{" "}
            <Link to="/privacy-policy">Privacy Policy</Link>.
          </p>
        </div>
      </div>

      {showError && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-message">{errorMessage}</div>
            <div className="modal-actions">
              <button className="modal-btn confirm" onClick={handleCloseError}>OK</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login; 